import { DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { FC, useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { v4 } from 'uuid';
import { FormPageModel } from '../../../../../shared/kiosk';
import { MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { classNames } from '../../../../utils';
import { RootFormState, SetRoot } from '../FormElements';

interface PagesToolboxProps {
  readonly state: RootFormState;
  readonly setState: SetRoot;
}

function findIndex(pages: FormPageModel[], id: string) {
  return pages.findIndex((p) => p.id === id);
}

export const PagesToolbox: FC<PagesToolboxProps> = ({ state, setState }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { ts } = useTranslation();

  const onDragEnd = useCallback(
    ({ over }: DragEndEvent) => {
      setActiveId(null);
      if (over) {
        const activeIndex = activeId ? findIndex(state.layout.pages, activeId) : -1;
        const overIndex = findIndex(state.layout.pages, over.id as string);
        if (activeIndex !== overIndex) {
          setState({
            ...state,
            layout: {
              ...state.layout,
              pages: arrayMove(state.layout.pages, activeIndex, overIndex),
            },
          });
        }
      }
    },
    [state, activeId, setActiveId, setState],
  );

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setActiveId(active.id as string);
    },
    [setActiveId],
  );

  const addPage = useCallback(() => {
    const activePageExists = state.layout.pages.some((t) => t.id === state.activePage);
    const newId = v4();

    setState({
      ...state,
      activePage: activePageExists ? state.activePage : newId,
      layout: {
        ...state.layout,
        pages: [
          ...state.layout.pages,
          {
            id: newId,
            name: '',
            sections: [],
          },
        ],
      },
    });
  }, [state, setState]);

  const editPage = useCallback(
    (pageId: string) => {
      setState((oldState) => ({
        ...oldState,
        activePage: pageId,
      }));
    },
    [setState],
  );

  const deletePage = useCallback(
    (pageId: string) => {
      setState((oldState) => {
        const newPages = oldState.layout.pages.filter((p) => p.id !== pageId);
        const { activePage } = oldState;
        return {
          ...oldState,
          // Reset to an existing page
          activePage: activePage === pageId ? (newPages[0]?.id ?? null) : activePage,
          layout: {
            ...oldState.layout,
            pages: newPages,
          },
        };
      });
    },
    [setState],
  );

  return (
    <div>
      <DndContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <SortableContext items={state.layout.pages}>
          {state.layout.pages.map((page) => {
            return (
              <PageItem
                deletePage={deletePage}
                editPage={editPage}
                icon="abc"
                key={page.id}
                page={page}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <PageItem editPage={editPage} icon="F" page={state.layout.finalPage} />
      <Button onClick={addPage}>{ts('add_page')}</Button>
    </div>
  );
};

interface PageItemProps {
  readonly icon: string;
  readonly isDragOverlay?: boolean;
  readonly page: FormPageModel;
  readonly deletePage?: (pageId: string) => void;
  readonly editPage: (pageId: string) => void;
}

const PageItem: FC<PageItemProps> = ({ icon, page, isDragOverlay, editPage, deletePage }) => {
  const { ts } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(
        {
          dragging: isDragOverlay,
          'is-dragging': isDragging,
        },
        'editor-toolbox-item',
      )}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {deletePage && (
        <div className="handlebar" ref={setActivatorNodeRef} {...listeners}>
          <MaterialIcon name="drag_indicator" />
        </div>
      )}
      <div className="icon">
        <MaterialIcon name={icon} />
      </div>
      <div className="content">
        <strong>{page.name === '' ? '(Empty)' : page.name}</strong>
        <div className="description">{ts('pagesectionslength_sections', page.sections.length)}</div>
      </div>
      <Button
        onClick={() => {
          editPage(page.id);
        }}
      >
        <MaterialIcon name="edit" />
      </Button>
      {deletePage && (
        <Button
          onClick={() => {
            deletePage(page.id);
          }}
        >
          <MaterialIcon name="delete" type="danger" />
        </Button>
      )}
    </div>
  );
};
